import React, { useEffect, useRef, useState } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import { useLocation } from 'react-router-dom';
import { useHistory } from '@useHistory/useHistoryCompat';
import * as Yup from 'yup';
import { POST_LOGIN_REDIRECT_URL, SETUP_PERSONA_REQUIRED } from '../../constants/auth';
import { GoogleAuthRequestType, HdAuthRequestType, ResetPasswordRequestType } from '../../interfaces/auth';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { HdFormikPasswordField } from '../../../form/formik/components';
import { HdResolvedComponent } from '../../../routing/components/resolved-component/HdResolvedComponent';
import { HdPane } from '../../../components';
import HdButton from '../../../button/components/button/HdButton';
import HdFormControl from '../../../form/components/form-control/HdFormControl';
import ErrorFocus from '../../../form/components/error-focus/ErrorFocus';
import { GoogleLoginButton } from '../google-login-button/GoogleLoginButton';
import { HdAuth } from '../auth-layout/AuthLayout';
import { FeaturedCustomers } from '../testimonial-section/TestimonialSection';
import { useFeaturedCustomers } from '../../hooks/useFeaturedCustomer';
import { useGoogleLoginError } from '../../hooks/useGoogleLoginError';
import { useResetPassword } from '../../hooks/useResetPassword';
import useService from '../../../injector/hooks/useService';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { useVerifyEmail } from '../../hooks/useVerifyEmail';
import { StrongPasswordRulesWrapper } from '../../../input/components/strong-password-rules-wrapper/StrongPasswordRulesWrapper';
import { PASSWORD_RULES_SET } from '../../../input/components/strong-password-rules-wrapper/contants';
import { StrongPasswordFieldWrapper } from '../../../input/components/strong-password-rules-wrapper/StrongPasswordFieldWrapper';
import { strongPasswordRegex } from '../../../input/components/strong-password-rules-wrapper/ruleValidators';

export interface AcceptInviteProps {
  featuredCustomers: FeaturedCustomers;
  email: string;
}

const initialValues = {
  password: ''
};

const validationSchema = Yup.object({
  password: Yup.string()
    .strict(true)
    .trim('Password cannot start or end with whitespaces')
    .matches(strongPasswordRegex)
    .required('Password is required')
});

export function AcceptInvite() {
  const { getFeaturedCustomers } = useFeaturedCustomers();
  const { verifyEmail } = useVerifyEmail();

  return (
    <HdResolvedComponent
      Component={AcceptInviteInner}
      resolve={{
        featuredCustomers: getFeaturedCustomers,
        email: verifyEmail
      }}
    />
  );
}

export function AcceptInviteInner({ featuredCustomers, email }: AcceptInviteProps) {
  const [formError, setFormError] = useState(null);
  const [googleLoginError, setGoogleLoginError] = useState(useGoogleLoginError());

  const formikRef = useRef<FormikProps<{ password: string }>>();
  const localStorage = useService(LocalStorageService);
  const { resetPassword } = useResetPassword();
  const { search } = useLocation();
  const history = useHistory();

  const code = new URLSearchParams(search).get('code');

  useEffect(() => {
    /**
     * setting the local storage variable
     * which can define if user has signed up via invite flow
     * so that we can force user to setup persona page
     *
     * and router adding the query param next to handle the
     * page user should land post successful auth
     *
     * if any existing next param is set then capturing that in LS
     */
    const searchParams = new URLSearchParams(search);
    const next = searchParams.get('next');
    searchParams.delete(next);
    searchParams.set('next', '/setup-persona');
    localStorage.set(SETUP_PERSONA_REQUIRED, true);
    localStorage.set(POST_LOGIN_REDIRECT_URL, next);

    history.replace({ search: searchParams.toString() });
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    if (!formikRef.current.isSubmitting) {
      return;
    }

    setFormError(null);
    setGoogleLoginError(null);

    resetPassword(code, values.password, ResetPasswordRequestType.ACCEPT_INVITE).then(
      () => {},
      error => {
        setSubmitting(false);

        const errorMessage = getErrorMessageFromObj(error);
        setFormError(errorMessage);
      }
    );
  };

  return (
    <HdAuth
      selectedAuthType={HdAuthRequestType.ACCEPT_INVITE}
      featuredCustomers={featuredCustomers}
    >
      <GoogleLoginButton code={code} requestType={GoogleAuthRequestType.ACCEPT_INVITE} />

      {!!googleLoginError && (
        <HdPane
          className='mt-4 w-100'
          variant='error-faded'
          icon='error-filled'
          iconClasses='text-error'
          disableHide
        >
          <div className='text-default'>{googleLoginError}</div>
        </HdPane>
      )}

      <div className='separator-with-content social-login-separator'>
        <span className='separator-content'>OR</span>
      </div>

      <div className='email-display text-center'>
        Log in with your email ID &nbsp;
        {!!email && <span className='text-default text-bold'>{email}</span>}
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
        innerRef={formikRef}
      >
        {props => {
          const { isValid, isSubmitting } = props;
          const disableSubmit = !isValid || isSubmitting;

          return (
            <form noValidate onSubmit={props.handleSubmit}>
              <ErrorFocus formik={props} />

              <HdFormControl className='mb-0'>
                <StrongPasswordFieldWrapper
                  title={StrongPasswordRulesWrapper}
                  rules={PASSWORD_RULES_SET}
                  value={formikRef.current?.values?.password}
                >
                  <Field
                    name='password'
                    label='Password'
                    placeholder='Enter Password'
                    component={HdFormikPasswordField}
                    prefixIcon='password'
                    autoFocus
                    required
                  />
                </StrongPasswordFieldWrapper>
              </HdFormControl>

              {!!formError && (
                <HdPane
                  className='mb-1 w-100'
                  variant='error-faded'
                  icon='error-filled'
                  iconClasses='text-error'
                  disableHide
                >
                  <div className='text-default'>{formError}</div>
                </HdPane>
              )}

              <HdButton
                type='submit'
                size='lg'
                className='w-100 mt-4'
                dataId='accept-invite-set-password'
                showProgress={isSubmitting}
                disabled={disableSubmit}
              >
                {isSubmitting ? 'Setting Password' : 'Set Password'}
              </HdButton>
            </form>
          );
        }}
      </Formik>
    </HdAuth>
  );
}
